/**
 * Группы символов и порядок их открытия.
 *
 * Механизм тот же, что в «Основах»: сразу весь цифровой ряд или все знаки
 * препинания — это тот же обескураживающий экран, ради ухода от которого
 * режимы и разделены. Различается только то, как режим делит свой набор:
 * цифры — по парам одноимённых пальцев, остальные — по два символа подряд в
 * порядке важности.
 */

import type { MasteryUnit } from '@type2x/shared-contracts';
import { toMasteryUnitId } from '@type2x/shared-contracts';
import {
  FINGER_PAIRS,
  fingerPairOf,
  getLayout,
  strokeForCharacter,
  type FingerPair,
} from '@type2x/keyboard-layouts';
import { characterFrequency } from '@type2x/corpora';
import {
  currentUnlockableGroup,
  masteredGroups,
  unlockedGroups,
  type UnlockableGroup,
} from '@type2x/learning-engine';

import type { ShiftHint, SymbolGroup, SymbolMode, SymbolsSnapshot } from '../contracts/symbols.js';
import { GROUP_SIZE } from '../contracts/symbols.js';
import { digitCharacters, modeCharacters } from './sets.js';

/** Учебная единица символа: единица режима — клавиша, как и в «Основах». */
export function symbolUnit(snapshot: SymbolsSnapshot, character: string): MasteryUnit {
  return {
    id: toMasteryUnitId(`key:${character}`),
    kind: 'key',
    content: character,
    language: snapshot.language,
    layoutId: snapshot.layoutId,
    realWorldFrequency: characterFrequency(snapshot.language, character),
  };
}

/** Группа в виде, понятном общему механизму открытия. */
function unlockable(group: SymbolGroup): UnlockableGroup {
  return {
    id: group.id,
    unitIds: group.characters.map((character) => toMasteryUnitId(`key:${character}`)),
  };
}

/** Пара пальцев, которой набирается символ. */
function pairOfCharacter(snapshot: SymbolsSnapshot, character: string): FingerPair | null {
  const stroke = strokeForCharacter(getLayout(snapshot.layoutId, snapshot.platform), character);
  return stroke === null ? null : fingerPairOf(stroke.key.finger);
}

/** Цифры парами одноимённых пальцев: от указательных к мизинцам. */
function digitGroups(snapshot: SymbolsSnapshot): SymbolGroup[] {
  const digits = digitCharacters(snapshot);

  return FINGER_PAIRS.map((pair) => ({
    id: `digits-${pair}`,
    mode: 'digits' as const,
    characters: digits.filter((digit) => pairOfCharacter(snapshot, digit) === pair),
  })).filter((group) => group.characters.length > 0);
}

/** Остальные наборы — по два символа в порядке важности. */
function chunkedGroups(snapshot: SymbolsSnapshot, mode: SymbolMode): SymbolGroup[] {
  const characters = modeCharacters(snapshot, mode);
  const groups: SymbolGroup[] = [];

  for (let start = 0; start < characters.length; start += GROUP_SIZE) {
    groups.push({
      id: `${mode}-${groups.length}`,
      mode,
      characters: characters.slice(start, start + GROUP_SIZE),
    });
  }

  return groups;
}

/** Все группы режима в порядке открытия. */
export function symbolGroups(snapshot: SymbolsSnapshot, mode: SymbolMode): SymbolGroup[] {
  return mode === 'digits' ? digitGroups(snapshot) : chunkedGroups(snapshot, mode);
}

/** Группы режима, пройденные к этому моменту. */
export function completedGroups(snapshot: SymbolsSnapshot, mode: SymbolMode): SymbolGroup[] {
  const groups = symbolGroups(snapshot, mode);
  const mastered = new Set(masteredGroups(groups.map(unlockable), snapshot.stats).map((g) => g.id));
  return groups.filter((group) => mastered.has(group.id));
}

/** Группа, которую пользователь осваивает сейчас; null — пройдены все. */
export function currentGroup(snapshot: SymbolsSnapshot, mode: SymbolMode): SymbolGroup | null {
  const groups = symbolGroups(snapshot, mode);
  const current = currentUnlockableGroup(groups.map(unlockable), snapshot.stats);
  return groups.find((group) => group.id === current?.id) ?? null;
}

/** Символы режима, доступные упражнению: пройденные группы и текущая. */
export function unlockedCharacters(snapshot: SymbolsSnapshot, mode: SymbolMode): string[] {
  const groups = symbolGroups(snapshot, mode);
  const open = new Set(unlockedGroups(groups.map(unlockable), snapshot.stats).map((g) => g.id));

  return groups.filter((group) => open.has(group.id)).flatMap((group) => group.characters);
}

/** Есть ли режиму чему учить: остались непройденные группы. */
export function hasSymbols(snapshot: SymbolsSnapshot, mode: SymbolMode): boolean {
  return currentGroup(snapshot, mode) !== null;
}

/**
 * Подсказки о Shift для символов текста.
 *
 * Пользователю показывается не только символ, но и клавиша, которую Shift
 * меняет: без этого символ приходится искать глазами по всей клавиатуре
 * (ТЗ §6, режим 10).
 */
export function shiftHints(snapshot: SymbolsSnapshot, characters: Iterable<string>): ShiftHint[] {
  const layout = getLayout(snapshot.layoutId, snapshot.platform);
  const hints = new Map<string, ShiftHint>();

  for (const character of characters) {
    if (hints.has(character)) continue;

    const stroke = strokeForCharacter(layout, character);
    if (stroke === null || !stroke.requiresShift) continue;

    hints.set(character, {
      character,
      base: stroke.key.base,
      finger: stroke.key.finger,
    });
  }

  return [...hints.values()];
}
